/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// components
import styles from './DeviceLinks.module.css';
import { Background } from 'layout';
import { Loader } from 'ui';
import TimeUpModal from './components/TimeUpModal/TimeUpModal';
import {
    REAUTHENTICATE_ROUTE,
    VERIFICATION_FLOWS,
    REAUTHENTICATE_ROUTES,
    WORKFLOW_STATUS,
} from 'utils/constants';
import ProcessingView from './components/ProcessingView';
import VerificationView from './components/VerificationView';
// state
import { actions as statusActions, selectors as statusSelectors } from 'actions/status';
import {
    actions as reauthenticateStatusActions,
    selectors as reauthenticateStatusSelectors,
    getReauthenticateSessionStatus,
} from 'actions/reAuthenticate';
import { profileApis, useSetProgressMutation } from 'api/profile';
import { ROUTES } from 'utils/constants/routes';
import {
    selectProfile,
    selectVerificationDetails,
    selectIdVerifications,
} from 'store/common/selectors/profile';
import { selectVerificationConfiguration } from 'store/common/selectors/general';
import { selectSession } from 'features/onboarding/state/selectors';
import { selectReauthSessionId } from 'features/reauthenticate/state';

//TODO: Refactor the polling logic, component structure and excessive use of useEffect hooks while changing the verification options component.
const DeviceLinks = ({ verificationFlow, ...props }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Medallion flow state + polling
    const session = useSelector(selectSession);
    const profile = useSelector(selectProfile);
    const verificationDetails = useSelector(selectVerificationDetails);
    const idVerifications = useSelector(selectIdVerifications);
    const verificationConfiguration = useSelector(selectVerificationConfiguration);
    const lastPoint = useSelector(statusSelectors.getLastPoint);
    const count = useSelector(statusSelectors.getCount);

    // Reauthenticate flow state + polling
    const reAuthenticateSessionID = useSelector(selectReauthSessionId);
    const reauthenticateSessionStatus = useSelector(
        (state) => state.reAuthenticate?.reauthenticateSessionStatus
    );
    const reauthenticateLastPoint = useSelector(reauthenticateStatusSelectors.getLastPoint);
    const reauthenticateCount = useSelector(reauthenticateStatusSelectors.getCount);

    const startPolling = () => dispatch(statusActions.start());
    const stopPolling = () => dispatch(statusActions.stop());
    const resetPolling = () => dispatch(statusActions.reset());
    const [setStatus] = useSetProgressMutation();
    const stopReauthenticatePolling = () => dispatch(reauthenticateStatusActions.stop());
    const resetReauthenticatePolling = () => dispatch(reauthenticateStatusActions.reset());
    const getReauthenticateSessionStatusAction = (sessionID) =>
        dispatch(getReauthenticateSessionStatus(sessionID));
    const getProfile = () =>
        dispatch(
            profileApis.endpoints.getProfile.initiate(undefined, {
                forceRefetch: true,
                subscribe: false,
            })
        );

    const isReauthenticate = verificationFlow === VERIFICATION_FLOWS.REAUTHENTICATE;
    const sessionTheme = profile?.company?.theme || reauthenticateSessionStatus?.theme;
    // if the document is done, the link should open the selfie step, not the document capture
    const isSelfiePending =
        Boolean(idVerifications?.govt_id?.metadata?.livenessCheck?.enabled) &&
        Boolean(verificationDetails?.is_document_valid) &&
        (verificationDetails?.selfie_attempts ?? 0) === 0;

    const govtIdEntryRoute = isSelfiePending ? ROUTES.GOVT_ID_V2_CAPTURE_SELFIE : ROUTES.GOVT_ID_V2;

    // Generate the link according to session
    const LINK = useMemo(() => {
        const baseUrl = `${globalThis.location.protocol}//${globalThis.location.host}`;
        return isReauthenticate
            ? `${baseUrl}/${REAUTHENTICATE_ROUTE}/mobile?sessionUUID=${reAuthenticateSessionID}`
            : `${baseUrl}${govtIdEntryRoute}?token=${session.token}`;
    }, [isReauthenticate, reAuthenticateSessionID, session.token, govtIdEntryRoute]);

    const [cancelled, setCancelled] = React.useState(false);
    const [show, setShow] = React.useState(false);

    // The profile refresh below is awaited across a navigation this screen may not survive.
    const isMountedRef = React.useRef(true);
    React.useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        []
    );

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Reauthenticate flow starts 👇
    const isSelfieAttemptsUsed =
        reauthenticateLastPoint?.data?.selfieVerification?.attempts >=
        reauthenticateLastPoint?.data?.allowedAttempts;

    const isEmailAttemptsUsed =
        reauthenticateLastPoint?.data?.additionalVerification?.email?.attempts >=
        reauthenticateLastPoint?.data?.allowedAttempts;

    const isPhoneAttemptsUsed =
        reauthenticateLastPoint?.data?.additionalVerification?.phone?.attempts >=
        reauthenticateLastPoint?.data?.allowedAttempts;

    const emailFailed =
        reauthenticateLastPoint?.data?.additionalVerification?.email?.status === 'FAILED';
    const phoneFailed =
        reauthenticateLastPoint?.data?.additionalVerification?.phone?.status === 'FAILED';
    // `.status`, not `.attempts` — the sibling checks above read the same field, and `attempts` is a
    // count, so comparing it to a status string was never true and the redirect below leaned entirely
    // on the attempts-used check beside it.
    const selfieFailed = reauthenticateLastPoint?.data?.selfieVerification?.status === 'FAILED';

    const handleReauthenticateFailure = useCallback(() => {
        const currentStatus = reauthenticateLastPoint?.data?.status;

        if (currentStatus === 'COMPLETED' || currentStatus === 'FAILED') {
            if (currentStatus === 'COMPLETED') {
                stopReauthenticatePolling();
            } else if (selfieFailed || isSelfieAttemptsUsed) {
                navigate(REAUTHENTICATE_ROUTES.SELFIE_FAILURE);
                stopReauthenticatePolling();
            } else if (
                (emailFailed && isEmailAttemptsUsed) ||
                (phoneFailed && isPhoneAttemptsUsed)
            ) {
                navigate(REAUTHENTICATE_ROUTES.VERIFY_CODE_FAILURE);
                stopReauthenticatePolling();
            }
        } else if (reauthenticateCount > 60) {
            stopReauthenticatePolling();
            handleShow();
        }
    }, [reauthenticateLastPoint, reauthenticateCount]);

    const handleReauthenticatePolling = useCallback(() => {
        resetReauthenticatePolling();
        getReauthenticateSessionStatusAction(reAuthenticateSessionID);

        return () => resetReauthenticatePolling();
    }, []);

    const handleReauthenticateModalClose = () => {
        resetReauthenticatePolling();
        getReauthenticateSessionStatusAction(reAuthenticateSessionID);
        handleClose();
    };

    const handleReauthenticateSessionClose = () => {
        setCancelled(true);
        stopReauthenticatePolling();
    };

    // Reauthenticate flow ends 👆

    // Medallion flows starts 👇

    const bothEitherMethod =
        verificationConfiguration === 'AND' || verificationConfiguration === 'OR';

    // is Kba available or not
    const currentKbaAttempts = verificationDetails?.kba_attempts;
    const totalKbaAttempts = idVerifications?.kba?.attempts;
    const isKbaAttemptsUsed = currentKbaAttempts >= totalKbaAttempts;
    const isKbaVerified = verificationDetails?.is_kba_verified === 1;
    const isKbaAvailable =
        Boolean(idVerifications?.kba?.enabled) && !isKbaVerified && !isKbaAttemptsUsed;

    // is bank option available or not
    const isBankVerified = verificationDetails?.is_bank_owner_matched;
    const currentBankAttempts = verificationDetails?.bank_attempts;
    const totalBankAttempts = idVerifications?.bank?.attempts;
    const isBankAttemptsUsed = currentBankAttempts >= totalBankAttempts;
    const isBankAvailable =
        Boolean(idVerifications?.bank?.enabled) && !isBankAttemptsUsed && !isBankVerified;

    const govtIdAttempts = lastPoint?.data?.metadata?.govtIDAttempts;
    const maxIdAttempts = idVerifications?.govt_id?.attempts || 0;
    // An allowance we could not read is not an allowance of zero. Without the first term the
    // comparison is `attempts >= 0`, which holds for every value — including none spent — so a
    // verification response that has not landed yet reads as a user with nothing left.
    const limitExceed = maxIdAttempts > 0 && govtIdAttempts >= maxIdAttempts;

    const handleMedallionModalClose = () => {
        resetPolling();
        startPolling();
        handleClose();
    };

    const handleMedallionPolling = useCallback(() => {
        resetPolling();
        startPolling();
        return () => resetPolling();
    }, []);

    /**
     * A phone that lands here is not waiting on anything — it is the device the work happens on, so
     * it goes straight to the step the link points at.
     *
     * Held until the verification response has landed. Which step that is depends on it: a document
     * already captured sends the user to the selfie rather than back through a capture they have
     * done. Redirecting before it arrives would send them to the wrong one, and `location.replace`
     * leaves nothing to come back from.
     */
    React.useEffect(() => {
        if (!isMobile || isReauthenticate || !idVerifications) return;

        globalThis.location.replace(`${LINK}&source=MOBILE`);
    }, [isReauthenticate, idVerifications, LINK]);

    const handleMedallionSessionClose = () => {
        setCancelled(true);
        setStatus('IN_PROGRESS');
    };

    // Medallion flows ends 👆

    const handleModalClose = isReauthenticate
        ? handleReauthenticateModalClose
        : handleMedallionModalClose;

    /**
     * The document itself passed. Not the same as the phone being finished — a workflow that also
     * asks for a selfie carries on past this — but it does mean the user has no reason to be offered
     * a different verification method.
     */
    const isDocumentComplete = (response) =>
        response?.workflowStatus?.idVerifications?.govt_id?.status === 'COMPLETED';

    /**
     * True when the workflow asks for a selfie and it has neither passed nor used up its attempts,
     * i.e. the phone still has the selfie step left to do.
     *
     * Read from the selfie's own metadata, not from `govt_id.status` — that reads COMPLETED as
     * soon as the document is done, so it stays true for the whole selfie step.
     */
    const isSelfieOutstanding = useCallback(
        (response) => {
            const livenessCheck = idVerifications?.govt_id?.metadata?.livenessCheck;

            if (!livenessCheck?.enabled) return false;

            const govtId = response?.workflowStatus?.idVerifications?.govt_id;
            const allowedSelfieAttempts = livenessCheck?.attempts ?? 0;
            const usedSelfieAttempts = response?.metadata?.selfieAttempts ?? 0;

            /**
             * Nothing below means anything until a selfie has actually been taken. The verdict flags
             * are not absent beforehand — they are reported `false`, which reads as "nothing was
             * wrong" and would make an untaken selfie look like a passed one.
             */
            const selfieAttempted = govtId?.selfie_attempted === true || usedSelfieAttempts > 0;

            /**
             * The same two signals the phone decides on, so the two screens cannot disagree about
             * what happened: whether the check confirmed a live person, and whether the face matched.
             *
             * `is_liveness_score_low` is deliberately not consulted: the assessment already accounts
             * for the score, and the phone does not gate on it either.
             */
            const selfieResult = govtId?.metadata;
            const selfiePassed =
                selfieAttempted &&
                selfieResult?.is_liveness_assessment_not_live === false &&
                selfieResult?.is_face_match_score_low === false;

            /**
             * A failed selfie does not block the document result — with nothing left to retry the
             * phone carries on to it, so this screen has to as well. Without this the desktop would
             * wait out its whole poll budget while the phone had already moved on.
             */
            const selfieExhausted =
                allowedSelfieAttempts > 0 && usedSelfieAttempts >= allowedSelfieAttempts;

            // A failure with attempts still to come is not an ending: the phone is showing a retry.
            return !selfiePassed && !selfieExhausted;
        },
        [idVerifications]
    );

    /**
     * True when the phone has finished everything this workflow asks of it, which is what tells
     * this screen to stop polling: the document status when no selfie is configured, the selfie
     * result when one is.
     */
    const checkGovtIdStatus = useCallback(
        (response) => {
            const selfieCheckEnabled = idVerifications?.govt_id?.metadata?.livenessCheck?.enabled;

            // No selfie in this workflow, so the document is the whole answer.
            if (!selfieCheckEnabled) return isDocumentComplete(response);

            return !isSelfieOutstanding(response);
        },
        [idVerifications, isSelfieOutstanding]
    );

    //Stops polling, refetches the profile, then navigates to `to`.
    const leaveFor = useCallback(
        async (to) => {
            stopPolling();
            await getProfile()
                .unwrap()
                .catch(() => undefined);

            if (isMountedRef.current) navigate(to);
        },
        [navigate]
    );

    React.useEffect(() => {
        setCancelled(false);
        if (!isReauthenticate && !profile.loading) {
            const response = lastPoint?.data;
            const { ERROR, IN_PROGRESS } = WORKFLOW_STATUS;

            /**
             * Gates every exit below: while the selfie is still to be done, the phone is ahead of
             * this screen and there is nothing to navigate to. `response.status` is not a
             * substitute — it reports the document transaction, so it can read COMPLETED here.
             */
            const selfieOutstanding = isSelfieOutstanding(response);

            if (
                !selfieOutstanding &&
                (response?.status === 'COMPLETED' || checkGovtIdStatus(response))
            ) {
                leaveFor(-1);
            } else if (limitExceed && !isDocumentComplete(response)) {
                const govtId = response?.workflowStatus?.idVerifications?.govt_id;

                /**
                 * `govtIdAttempts` counts the attempts made; the status map is keyed by their index,
                 * so the most recent one is the count minus one. Read at the count it always missed —
                 * which left the IN_PROGRESS check below with nothing to hold on, and the ERROR check
                 * unable to fire at all.
                 */
                const lastAttemptStatus =
                    govtId?.document_attempts_status?.[`${govtIdAttempts - 1}`];

                /**
                 * Narrows `selfieOutstanding` to the cases where the selfie is actually reachable.
                 * The selfie is matched against the document, so an errored document has no selfie
                 * left to do and must fall through to the branches below.
                 */
                const selfieAhead = selfieOutstanding && lastAttemptStatus !== ERROR;

                /**
                 * Somewhere else to go beats a dead end, so this is tried first — and now has to be
                 * `else if` below. Until the index above was corrected the status never read as
                 * anything, so only this branch could ever run; both can now, and a user with a
                 * method left would otherwise be shown the failure screen on top of it.
                 */
                if (
                    !selfieAhead &&
                    bothEitherMethod &&
                    lastAttemptStatus !== IN_PROGRESS &&
                    (isKbaAvailable || isBankAvailable)
                ) {
                    leaveFor('/verification');
                } else if (lastAttemptStatus === ERROR) {
                    leaveFor('/verification/idFailed');
                }
            } else if (count > 60) {
                stopPolling();
                handleShow();
            }
        } else {
            handleReauthenticateFailure();
        }
    }, [lastPoint, count, handleReauthenticateFailure, isReauthenticate]);

    React.useEffect(() => {
        if (isReauthenticate) {
            handleReauthenticatePolling();
        } else {
            handleMedallionPolling();
        }
    }, [handleMedallionPolling, handleReauthenticatePolling, isReauthenticate]);

    let verificationOption = 'OR';

    if (verificationConfiguration) {
        verificationOption = verificationConfiguration;
    }

    const handleSessionClose = isReauthenticate
        ? handleReauthenticateSessionClose
        : handleMedallionSessionClose;

    const handleBackClick = () => {
        if (!isReauthenticate) getProfile();
        globalThis.history.back();
    };

    if (isMobile) {
        return (
            <Background className={styles.deviceLinkTabs}>
                <Loader />
            </Background>
        );
    }

    const isProcessing = isReauthenticate
        ? reauthenticateLastPoint?.data?.status === 'IN_PROGRESS'
        : lastPoint?.data?.status !== 'IN_PROGRESS';

    return (
        <Background className={styles.deviceLinkTabs}>
            <TimeUpModal show={show} onHide={handleModalClose} />
            {isProcessing ? (
                <ProcessingView handleSessionClose={handleSessionClose} isDisabled={cancelled} />
            ) : (
                <VerificationView
                    verificationOption={verificationOption}
                    isReauthenticate={isReauthenticate}
                    sessionTheme={sessionTheme}
                    link={LINK}
                    handleBackClick={handleBackClick}
                    stopPolling={stopPolling}
                    navigate={navigate}
                    {...props}
                />
            )}
        </Background>
    );
};

DeviceLinks.propTypes = {
    verificationFlow: PropTypes.string,
};

export default DeviceLinks;
